import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./NotificationBell.css";

const NotificationBell = ({ baseUrl, adminCategory, user, isAssistant }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchNotifications = async () => {
    if (!isAssistant && (!adminCategory || adminCategory.length === 0)) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const categoriesParam = Array.isArray(adminCategory)
        ? adminCategory.join(",")
        : adminCategory;
      const params = { categories: categoriesParam };

      if (isAssistant && user?.email) {
        params.email = user.email;
        params.isAssistant = "true";
      }

      const res = await axios.get(`${baseUrl}/admin-api/get-admin-notifications`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
       // Get all notifications
    const allNotifications = res.data.notifications || [];

    // Only keep notifications from the last 10 days
    const tenDaysAgo = new Date();
    tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);

    const recentNotifications = allNotifications.filter((notification) => {
      const timestamp =
        notification.timestamp || notification.lastCommentAt;

      if (!timestamp) return false;

      const notificationDate = new Date(timestamp);

      return notificationDate >= tenDaysAgo;
    });

    setNotifications(recentNotifications);

    } catch (error) {
      console.error("Error fetching admin notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [adminCategory, isAssistant, user]);

  const markNotificationsAsRead = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const categoriesParam = Array.isArray(adminCategory)
        ? adminCategory.join(",")
        : adminCategory;

      await axios.post(
        `${baseUrl}/admin-api/mark-notifications-read`,
        {
          categories: categoriesParam,
          email: user?.email,
          isAssistant,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  const handleNotificationClick = (notification) => {
    const complaintId = notification.complaintId || notification.complaint_id;
    if (complaintId) {
      navigate(`/complaints-details/${complaintId}`);
    }
    setShowDropdown(false);
    setTimeout(() => {
      fetchNotifications();
    }, 1500);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  const unreadCount = notifications.filter((item) => !item.isRead).length;

  const toggleDropdown = async () => {
    if (!showDropdown && unreadCount > 0) {
      await markNotificationsAsRead();
    }
    setShowDropdown(!showDropdown);
  };

  return (
    <div className="notification-bell-container">
      <button className="notification-bell-btn" onClick={toggleDropdown} title="View notifications">
        🔔
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </button>

      {showDropdown && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <h5>Admin Notifications</h5>
            <button className="close-btn" onClick={() => setShowDropdown(false)}>
              ✕
            </button>
          </div>

          {isLoading ? (
            <div className="notification-content">
              <p className="text-center text-muted">Loading...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="notification-content">
              <p className="text-center text-muted">No notifications</p>
            </div>
          ) : (
            <div className="notification-content">
              {notifications.map((notification, index) => {
                const isDuplicateSupport = notification.type === "duplicate_support";
                const complaintId = notification.complaintId || notification.complaint_id;
                const title = isDuplicateSupport
                  ? "🔥 Complaint Priority Increased"
                  : "🔄 Complaint Reopened";
                const message = notification.message || "New update available.";
                const timestamp = notification.timestamp || notification.lastCommentAt;

                return (
                  <div
                    key={notification._id || complaintId || index}
                    className={`notification-item ${notification.isRead ? "" : "unread"}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-title">{title}</div>
                    <div className="notification-message">Complaint #{complaintId}</div>
                    <div className="notification-message">{notification.title}</div>
                    <div className="notification-message">{message}</div>
                    {isDuplicateSupport && notification.supportCount !== undefined && (
                      <div className="notification-message">Support Count: {notification.supportCount}</div>
                    )}
                    <div className="notification-timestamp">{formatDate(timestamp)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
